<script>
/**
 * Linha do cliente que dá ESCOPO à conversa, acima das mensagens.
 *
 * Componente burro: recebe o cliente por prop e não lê store — é o que o mantém
 * montável sem sessão no teste, mesma disciplina de `chat-sidebar.vue`.
 *
 * 14/09/2026 (print do usuário): o cartão de 3 linhas ("Cliente em foco" /
 * nome + código + badge do agente / cadeado com o motivo) era GRANDE demais
 * para um bloco que é contexto, não conteúdo. Virou UMA linha: ponto de
 * status, nome + código, separador, o que o escopo cobre, ações à direita.
 * - O título saiu: a linha inteira já diz o que é.
 * - O badge do agente e o cadeado FICAM na linha (o usuário pediu de volta em
 *   14/09/2026): badge depois do código; travado, cadeado + o motivo no meio.
 * - A linha do cadeado saiu: travado vira o `title` da linha + ausência do X.
 * - O aviso do servidor ("Esta conversa fala de X") vira uma segunda linha
 *   discreta, com o "Trocar" na LINHA PRINCIPAL (é a ação; o texto é só o
 *   porquê). Escolhido isto, e não o aviso só no `title` do "Trocar": é a
 *   única pista de que o servidor identificou OUTRO cliente, e tooltip não
 *   é descoberto nem lido por leitor de tela.
 *
 * ⚠️ Mostra só o que o repo REALMENTE tem sobre o cliente (`user/allClients`:
 * `name`, `id`, `perfil`). O protótipo exibe também AuC e data da última
 * reunião — esses NÃO vêm nessa lista, e preenchê-los com número inventado num
 * cartão que nomeia um cliente de verdade é pior que não mostrá-los. Quando a
 * fonte existir, entram por prop, sem mexer no resto.
 */
import { formatBrokerCode } from '@utils/cliente-codigo/cliente-codigo'
import AgentBadge, {
  ESTADO_SUCESSO,
  ESTADO_VAZIO,
} from '@modules/aawz-intelligence-v2/components/agent-badge/agent-badge.vue'
import { ESCOPO_CLIENTE_TEXT } from '@modules/aawz-intelligence-v2/constants/Text.escopo-cliente'

const TAMANHO_ICONE = 12

export default {
  name: 'ChatClienteEscopo',
  components: { AgentBadge },
  props: {
    cliente: { type: Object, required: true },
    // ★ Conversa com histórico: o cliente não muda mais. O backend vincula na
    // CRIAÇÃO e não re-vincula quem retoma, então "remover" aqui tiraria a
    // linha da tela sem tirar o cliente da conversa — o pior dos dois mundos,
    // porque a próxima resposta continuaria escopada nele. O chip do composer
    // trava pelo mesmo motivo; esta linha é a OUTRA porta para a mesma ação.
    travado: { type: Boolean, default: false },
    // spec-009 [T008] — o agente ativo desta conversa: vira o `AgentBadge`
    // depois do código (`mostraAgenteBadge`, só com `hub` conhecido — ver
    // cabeçalho; o badge voltou à linha em 14/09/2026).
    agente: { type: Object, default: null },
    // spec-009 [T006/US-2, AC-2.3] — "Esta conversa fala de <nome>", quando
    // o chip está fixado MANUALMENTE em outro cliente e o servidor acabou de
    // identificar um diferente. Vazio ⇒ sem aviso nem "Trocar" (o caso comum).
    avisoDeConversa: { type: String, default: '' },
    // ★ Extensão além de `contracts/ui-components.md` §2 (que só declara
    // `avisoDeConversa: String`): o evento `trocar-para-cliente` do contrato
    // carrega `{id_cliente_app}`, e o componente é burro por desenho — não
    // resolve nome→id sozinho. A VIEW já tem esse id
    // (`clienteDaConversaAtualValidado`) no mesmo instante em que monta o
    // texto do aviso; repassá-lo aqui evita a linha adivinhar.
    idClienteDoAviso: { type: [Number, String], default: null },
    // ★ Bug ao vivo (10/09/2026): sem mensagem nenhuma ainda, a linha
    // esticava para a largura do trilho CHEIO (`--ia-chat-trilho-largura`,
    // 1024px), quando ao lado dela o herói/composer do estado vazio seguem o
    // trilho VAZIO (`--ia-chat-trilho-largura-vazio`, `empty-messages.vue`),
    // mais estreito. `estreito` é o mesmo `!hasMessages` que a view já usa
    // para decidir a posição (`escopoAcimaDasMensagens`) — aqui só governa a
    // LARGURA, reusando os dois tokens do trilho em vez de um valor novo.
    estreito: { type: Boolean, default: false },
  },
  data() {
    return { TAMANHO_ICONE }
  },
  computed: {
    nome() {
      return this.cliente.name || this.cliente.nome || ''
    },
    /**
     * `12345 · Perfil moderado` — só os pedaços que existem, sem separador solto.
     *
     * ★ CÓDIGO DA CORRETORA, não o `id` interno: é o identificador que o
     * assessor reconhece. Via `formatBrokerCode` porque o campo não é confiável
     * na origem — ver o cabeçalho do util.
     */
    detalhe() {
      const partes = []
      const codigo = formatBrokerCode(this.cliente.codigo_corretora)
      if (codigo) partes.push(codigo)
      if (this.cliente.perfil) partes.push(this.cliente.perfil)
      return partes.join(' · ')
    },
    // spec-009 [T006/US-2] — burro por desenho: não decide QUEM é o cliente
    // divergente, só repassa o id que a view já resolveu no aviso.
    temAviso() {
      return Boolean(this.avisoDeConversa)
    },
    // [G-3, design it.3 → it.4] O motivo do travamento só aponta para o
    // "Trocar" quando ele ESTÁ na tela (há aviso). Sem aviso, a frase mandava
    // usar um botão inexistente (print do usuário, 14/09/2026) — aí vale o
    // conselho genérico do chip/palette. `ESCOPO_CLIENTE_TEXT` segue a fonte
    // única dos dois textos.
    motivoTravamento() {
      return this.temAviso
        ? ESCOPO_CLIENTE_TEXT.motivoTravamentoComTroca
        : ESCOPO_CLIENTE_TEXT.motivoTravamento
    },
    // Destravado a linha não tem tooltip nenhum: `title` vazio some do DOM.
    // spec-009 [T008] — mesma régua de `escopo-select.vue`: sem hub
    // conhecido, o cartão não instancia o badge (AC-4.5). O badge VOLTOU à
    // linha compacta (pedido do usuário, 14/09/2026: "antes tinha o cadeado e
    // o IA Operacional ali").
    mostraAgenteBadge() {
      return Boolean(this.agente && this.agente.hub)
    },
    estadoDoAgenteBadge() {
      return this.agente && this.agente.agentSlug ? ESTADO_SUCESSO : ESTADO_VAZIO
    },
    // O texto do meio SÓ com copy que o cartão já tinha: travado, o motivo
    // ("Cliente fixado nesta conversa — …", condicional ao Trocar); livre, o
    // rótulo "Cliente em foco" que era o título das 3 linhas.
    textoDoMeio() {
      return this.travado ? this.motivoTravamento : ESCOPO_CLIENTE_TEXT.rotulo
    },
    tituloDaLinha() {
      return this.travado ? this.motivoTravamento : null
    },
  },
  methods: {
    trocarParaClienteDoAviso() {
      this.$emit('trocar-para-cliente', {
        id_cliente_app: this.idClienteDoAviso,
      })
    },
  },
}
</script>

<template>
  <div
    class="cliente-escopo ia-texto-corpo px-3 py-1"
    :class="{ 'cliente-escopo--estreito': estreito }"
    :title="tituloDaLinha"
  >
    <div class="cliente-escopo__linha">
      <span class="cliente-escopo__status" aria-hidden="true" />
      <span class="cliente-escopo__nome">{{ nome }}</span>
      <span v-if="detalhe" class="cliente-escopo__detalhe">{{ detalhe }}</span>
      <AgentBadge
        v-if="mostraAgenteBadge"
        :hub="agente.hub"
        :agent-slug="agente.agentSlug || ''"
        :motivo="agente.motivo || ''"
        :estado="estadoDoAgenteBadge"
      />
      <span class="cliente-escopo__separador" aria-hidden="true" />
      <icon-svg
        v-if="travado"
        name="lock"
        :size="TAMANHO_ICONE"
        color="var(--neutral-600)"
        class="flex-shrink-0"
      />
      <span class="cliente-escopo__resumo">{{ textoDoMeio }}</span>

      <span class="cliente-escopo__acoes">
        <!-- spec-009 [T006/US-2, AC-2.3] — guard do chip manual: quando o
             servidor identifica um cliente DIFERENTE do fixado, o chip
             continua no fixado e este botão oferece a troca explícita. O
             porquê vai na segunda linha (`__aviso`) e repete no `title`. -->
        <button
          v-if="temAviso"
          type="button"
          class="cliente-escopo__acao ia-texto-corpo cliente-escopo__acao--trocar"
          :title="avisoDeConversa"
          @click="trocarParaClienteDoAviso"
        >
          Trocar
        </button>

        <button
          type="button"
          class="cliente-escopo__acao ia-texto-corpo cliente-escopo__acao--perfil az-ds-hyperlink"
          title="Ver perfil do cliente"
          @click="$emit('abrir-perfil', cliente)"
        >
          Ver perfil
          <icon-svg
            name="arrow-left"
            :size="TAMANHO_ICONE"
            color="var(--primary)"
          />
        </button>

        <button
          v-if="!travado"
          type="button"
          class="cliente-escopo__acao ia-texto-corpo cliente-escopo__acao--limpar"
          title="Voltar a conversar sobre o escritório"
          aria-label="Remover o cliente do escopo"
          @click="$emit('limpar')"
        >
          <icon-svg
            name="close"
            :size="TAMANHO_ICONE"
            color="var(--neutral-700)"
          />
        </button>
      </span>
    </div>

    <div v-if="temAviso" class="cliente-escopo__aviso ia-texto-auxiliar">{{
      avisoDeConversa
    }}</div>
  </div>
</template>

<style lang="sass" scoped>
// Segue o trilho de leitura das mensagens (token `--ia-chat-trilho-largura`
// em `design/_ia-chat-trilho.sass` — fonte única, não repetir o número
// aqui): a linha é contexto DA conversa, então precisa ficar na mesma
// coluna que ela — solta na largura cheia, lia como um cabeçalho de página.
//
// ★ Sem conversa ainda, a coluna ao lado (herói/composer do estado vazio,
// `empty-messages.vue`) segue o trilho VAZIO, mais estreito — o modificador
// `--estreito` (prop `estreito`) troca para o MESMO token
// (`--ia-chat-trilho-largura-vazio`), sem inventar um valor novo.
//
// Superfície: o cinza da navbar/lateral (`--sidebar-warm-bg`) com a borda
// da mesma família — a linha lê como CHROME (contexto), não como conteúdo,
// que é o que a placa quase-branca anterior (`--ia-content-bg`) errava.
// Raio `--radius-md` (8px), não os 24 do composer: uma faixa de ~36px com
// raio 24 vira pílula. Sem `height` fixa — a versão flutuante da view
// (`.intelligence-v2-chat__escopo--flutuante`, `top: 0`) não reserva
// espaço, então a altura nasce só do conteúdo (13px + `py-1`).
//
// ⚠️ Texto muted em `--neutral-700`, NÃO `--neutral-600`: `_controls.sass:121`
// mede n600 sobre a sidebar em 4,39:1 (reprova AA); n700 passa.
.cliente-escopo
  width: 100%
  max-width: var(--ia-chat-trilho-largura)
  // O respiro superior separa a linha da barra de topo, que fica colada nela.
  margin: 16px auto 0
  background: var(--sidebar-warm-bg)
  border: 1px solid var(--sidebar-warm-border)
  border-radius: var(--radius-md)

.cliente-escopo--estreito
  max-width: var(--ia-chat-trilho-largura-vazio)

// O ponto substituiu a bola de 32px com ícone: é o que dava a altura da faixa
// antiga. Verde da marca, 8px — "há um cliente ativo", e só.
.cliente-escopo__status
  flex-shrink: 0
  width: 8px
  height: 8px
  border-radius: 50%
  background: var(--primary)

.cliente-escopo__linha
  display: flex
  align-items: center
  gap: 8px
  min-height: 28px
  min-width: 0

// Nome = a única ênfase da linha (600). Trunca antes do código, que é curto
// e fixo.
.cliente-escopo__nome
  flex-shrink: 1
  min-width: 0
  font-weight: 600
  color: var(--neutral-900)
  overflow: hidden
  white-space: nowrap
  text-overflow: ellipsis

.cliente-escopo__detalhe
  flex-shrink: 0
  color: var(--neutral-700)

.cliente-escopo__separador
  flex-shrink: 0
  align-self: stretch
  width: 1px
  margin: 4px 4px
  background: var(--sidebar-warm-border)

// O resumo é o que cede primeiro em tela estreita: é copy fixa, o usuário já
// a leu na primeira vez.
.cliente-escopo__resumo
  flex: 1 1 auto
  min-width: 0
  color: var(--neutral-700)
  overflow: hidden
  white-space: nowrap
  text-overflow: ellipsis

.cliente-escopo__acoes
  display: inline-flex
  align-items: center
  gap: 12px
  flex-shrink: 0
  margin-left: auto

// spec-009 [T006/US-2, AC-2.3] — segunda linha, abaixo da principal: o aviso
// não disputa com a identidade do cliente (que segue sendo o que se lê
// primeiro), mas precisa de cor própria para não ler como detalhe neutro.
//
// [G-1, design.json it.3, MUST] `var(--warning)` (#FF6433) como cor de TEXTO
// reprova AA para texto normal; `--warning-text-strong` (#8A5200 —
// `src/design/_controls.sass`, o par de TEXTO da família warning, já usado
// em `next-steps.vue`) passa com folga sobre o cinza da lateral.
.cliente-escopo__aviso
  color: var(--warning-text-strong)
  padding: 0 0 4px 16px

// "Ver perfil" tem cara de LINK (`az-ds-hyperlink`), não de botão: é navegação
// para outra tela, e um botão ali competia com o "Voltar para Gestão" da barra
// de topo. Segue sendo `<button>` — o precedente do repo para link que dispara
// ação (ver `fee-fixo-roleplay-consentimento`).
.cliente-escopo__acao
  display: inline-flex
  align-items: center
  gap: 4px
  flex-shrink: 0
  background: transparent

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

// "Trocar" é ação de correção, não navegação: peso do texto, cor do texto,
// sem cara de link — só sublinha no hover, como o "Trocar" do aviso antigo.
.cliente-escopo__acao--trocar
  font-weight: 600
  color: var(--neutral-900)

  &:hover
    text-decoration: underline

// O X é ação de descartar, não link: volta ao alvo clicável com respiro.
.cliente-escopo__acao--limpar
  padding: 6px
  border-radius: var(--radius-sm, 8px)

  &:hover
    background: var(--sidebar-warm-hover)
</style>
